import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { ordersAPI, vendeursAPI, driversAPI, usersListAPI, shopifyAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor, getStatusIcon, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
    Search,
    Plus,
    MoreVertical,
    Edit,
    Trash2,
    ExternalLink,
    RefreshCw,
    Clock,
    CheckCircle,
    Truck,
    Package,
    PackageX,
    XCircle,
    CheckSquare,
    X,
    AlertTriangle,
    StickyNote,
    PackageCheck,
    Minus,
    DollarSign,
    Loader2,
    ShoppingBag,
    Upload,
    Eye,
    MessageSquare,
    Phone,
    PhoneOff,
    Gift,
    Star,
    MapPin,
    Calendar,
    Tag,
    Copy,
    Check,
    Download
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const statusTabs = [
    { value: 'nouvelle', label: 'Nouvelles', icon: Clock, count: 0 },
    { value: 'confirme', label: 'Confirmées', icon: CheckCircle, count: 0 },
    { value: 'en_attente', label: 'En Attente', icon: Clock, count: 0 },
    { value: 'injoignable', label: 'Injoignable', icon: Phone, count: 0 },
    { value: 'rupture_stock', label: 'Rupture', icon: XCircle, count: 0 },
    { value: 'indisponible', label: 'Indisponible', icon: PackageX, count: 0 },
    { value: 'expedie', label: 'Expédiées', icon: Truck, count: 0 },
    { value: 'livre', label: 'Livrées', icon: Package, count: 0 },
    { value: 'annule', label: 'Annulées', icon: XCircle, count: 0 },
    { value: 'retourne', label: 'Retournées', icon: XCircle, count: 0 },
];

const statusOptions = [
    { value: 'nouvelle', label: 'Nouvelle' },
    { value: 'confirme', label: 'Confirmée' },
    { value: 'en_attente', label: 'En Attente' },
    { value: 'injoignable', label: 'Client Injoignable' },
    { value: 'rupture_stock', label: 'Rupture de Stock (temporaire)' },
    { value: 'indisponible', label: 'Indisponible (discontinué)' },
    { value: 'expedie', label: 'Expédiée' },
    { value: 'livre', label: 'Livrée' },
    { value: 'annule', label: 'Annulée' },
    { value: 'retourne', label: 'Retournée' },
];

const emptyOrder = {
    n_commande: '',
    date_commande: new Date().toISOString(),
    produit: '',
    produit_url: '',
    products: [], // New: array of products
    prix: 0,
    prix_achat: 0,
    full_name: '',
    phone: '',
    adresse: '',
    frais_livraison: '',  // Empty by default, not 0
    remise: '',  // Discount/Coupon
    remise_code: '',  // Coupon code (for tracking)
    prix_total: 0,
    status: 'nouvelle',
    observation: '',
    type_livraison: 'domicile',  // "domicile" or "bureau"
    service_livraison: '',  // "zr_express", "yalidine", "dhd"
    assigned_vendeur_id: '',
    assigned_driver_id: '',
    vendor_fees: [], // Additional fees (internal)
    pa_fees: [], // BBM fees (internal)
};

// Delivery type options
const typeLivraisonOptions = [
    { value: 'domicile', label: '🏠 À Domicile' },
    { value: 'bureau', label: '🏢 Bureau (Stop Desk)' },
];

// Delivery service options
const serviceLivraisonOptions = [
    { value: '', label: '-- Sélectionner --' },
    { value: 'zr_express', label: 'ZR Express' },
    { value: 'yalidine', label: 'Yalidine' },
    { value: 'dhd', label: 'DHD' },
];

export default function Orders() {
    const { hasPermission, user } = useAuth();
    const canEditPrixAchat = hasPermission('edit_prix_achat');
    const canManageLivreur = user?.role === 'admin' || hasPermission('manage_livreur');
    const [orders, setOrders] = useState([]);
    const [allOrders, setAllOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('nouvelle');
    const [statusCounts, setStatusCounts] = useState({
        nouvelle: 0, confirme: 0, en_attente: 0, injoignable: 0, rupture_stock: 0, 
        indisponible: 0, expedie: 0, livre: 0, annule: 0, retourne: 0
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [formData, setFormData] = useState(emptyOrder);
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [viewOrderOpen, setViewOrderOpen] = useState(false);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [dateFilter, setDateFilter] = useState('');
    const [vendeurs, setVendeurs] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [vendorFilter, setVendorFilter] = useState('all'); // Filter by vendor
    const [deliveryServiceFilter, setDeliveryServiceFilter] = useState('all'); // Filter by delivery service
    
    // Shopify product search states
    const [productSearch, setProductSearch] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const [showProductResults, setShowProductResults] = useState(false);
    const [shopifyConfigured, setShopifyConfigured] = useState(false);
    
    // Import file states
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [showAddProductForm, setShowAddProductForm] = useState(false);
    
    // Claim for vendor (admin) states
    const [claimForVendorDialogOpen, setClaimForVendorDialogOpen] = useState(false);
    const [orderToClaimFor, setOrderToClaimFor] = useState(null);
    const [selectedVendorForClaim, setSelectedVendorForClaim] = useState('');
    
    // Reject order (vendeur) states
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [orderToReject, setOrderToReject] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [suggestedPrice, setSuggestedPrice] = useState('');
    const [rejectNotes, setRejectNotes] = useState('');
    const [rejecting, setRejecting] = useState(false);
    
    // SMS confirmation states
    const [sendingSms, setSendingSms] = useState(null); // order id being processed
    const [confirmingManually, setConfirmingManually] = useState(null); // order id being confirmed
    const [markingInjoignable, setMarkingInjoignable] = useState(null); // order id being marked as injoignable
    const [sendingRuptureSms, setSendingRuptureSms] = useState(null); // order id for rupture SMS
    
    // Reward points states
    const [clientPoints, setClientPoints] = useState(null);
    const [pointsToUse, setPointsToUse] = useState(0);
    const [loadingPoints, setLoadingPoints] = useState(false);
    
    // Copy to clipboard state
    const [copiedId, setCopiedId] = useState(null);
    
    const handleCopyProduct = (text, orderId) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(orderId);
            toast.success('Nom du produit copié');
            setTimeout(() => setCopiedId(null), 2000);
        });
    };
    
    const handleExportCSV = () => {
        if (!orders.length) {
            toast.error('Aucune commande à exporter');
            return;
        }
        const statusLabel = statusTabs.find(t => t.value === activeTab)?.label || activeTab;
        const lines = [];
        lines.push('sep=;');
        lines.push('N° Commande;Produit;Prix');
        orders.forEach(o => {
            const produit = (o.produit || '').replace(/;/g, ',').replace(/"/g, '""').replace(/\n/g, ' ');
            lines.push(`${o.n_commande};"${produit}";${o.prix}`);
        });
        const bom = '\uFEFF';
        const csv = bom + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `commandes_${activeTab}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${orders.length} commandes "${statusLabel}" exportées`);
    };
    
    // Additional fees states
    const [newFeeType, setNewFeeType] = useState('vendor');
    const [newFeeAmount, setNewFeeAmount] = useState('');
    const [newFeeNote, setNewFeeNote] = useState('');
    
    // Check if user can assign orders
    const canAssign = user?.role === 'admin' || user?.role === 'office_agent' || user?.role === 'confirmateur' || user?.role === 'super_admin';
    
    // Check if user can add fees (admin, office_agent, or assigned/claimed vendor)
    const canAddFees = user?.role === 'admin' || user?.role === 'office_agent' || user?.role === 'confirmateur' || user?.role === 'super_admin' || 
        (user?.role === 'vendeur' && (editingOrder?.assigned_vendeur_id === user?.id || editingOrder?.claimed_by_vendeur_id === user?.id));
    
    // Check Shopify status on mount - assume configured by default
    useEffect(() => {
        const checkShopify = async () => {
            try {
                const response = await shopifyAPI.getStatus();
                setShopifyConfigured(response.data.configured);
            } catch (error) {
                // If check fails (auth issue), assume configured and let search handle errors
                console.log('Shopify status check skipped, assuming configured');
                setShopifyConfigured(true);
            }
        };
        checkShopify();
    }, []);
    
    // Debounced product search
    useEffect(() => {
        if (!productSearch || productSearch.length < 2) {
            setProductSearchResults([]);
            return;
        }
        
        const timer = setTimeout(async () => {
            setProductSearchLoading(true);
            console.log('🔍 Searching for:', productSearch);
            try {
                const response = await shopifyAPI.getProducts({ search: productSearch, limit: 15 });
                console.log('📦 API Response:', response);
                console.log('📦 Products found:', response.data?.products?.length || 0);
                const products = response.data?.products || [];
                setProductSearchResults(products);
                setShowProductResults(true);
                if (products.length === 0) {
                    console.log('⚠️ No products returned from API');
                }
            } catch (error) {
                console.error('❌ Error searching products:', error);
                console.error('❌ Error response:', error.response?.data);
                console.error('❌ Error status:', error.response?.status);
                setProductSearchResults([]);
            } finally {
                setProductSearchLoading(false);
            }
        }, 300);
        
        return () => clearTimeout(timer);
    }, [productSearch]);
    
    // Add product to order
    const handleAddProduct = useCallback((product) => {
        const currentProducts = formData.products || [];
        const existingIndex = currentProducts.findIndex(p => p.id === product.id);
        
        if (existingIndex >= 0) {
            // Increment quantity
            const updated = [...currentProducts];
            updated[existingIndex] = {
                ...updated[existingIndex],
                quantity: updated[existingIndex].quantity + 1
            };
            handleFormChange('products', updated);
        } else {
            // Add new product
            const newProduct = {
                id: product.id,
                product_id: product.product_id,
                title: product.full_title || product.title,
                price: product.price,
                quantity: 1,
                image_url: product.image_url,
                product_url: product.product_url,
                sku: product.sku,
            };
            handleFormChange('products', [...currentProducts, newProduct]);
        }
        
        setProductSearch('');
        setShowProductResults(false);
    }, [formData.products]);
    
    // Update product quantity
    const handleProductQuantityChange = useCallback((index, delta) => {
        const currentProducts = formData.products || [];
        const updated = [...currentProducts];
        const newQty = updated[index].quantity + delta;
        
        if (newQty <= 0) {
            updated.splice(index, 1);
        } else {
            updated[index] = { ...updated[index], quantity: newQty };
        }
        
        handleFormChange('products', updated);
    }, [formData.products]);
    
    // Remove product from order
    const handleRemoveProduct = useCallback((index) => {
        const currentProducts = formData.products || [];
        const updated = [...currentProducts];
        updated.splice(index, 1);
        handleFormChange('products', updated);
    }, [formData.products]);
    
    // Calculate products total (with proper parsing)
    const productsTotal = (formData.products || []).reduce((sum, p) => {
        const price = parseFloat(p.price) || 0;
        const qty = parseInt(p.quantity) || 1;
        return sum + (price * qty);
    }, 0);
    
    // Fetch orders and optionally sync from Shopify
    const fetchOrders = useCallback(async (syncShopify = false) => {
        try {
            setLoading(true);
            
            // If syncShopify is true and user is admin, sync from Shopify first
            if (syncShopify && (user?.role === 'admin' || user?.role === 'super_admin')) {
                try {
                    toast.info('Synchronisation Shopify en cours...');
                    await shopifyAPI.sync();
                    toast.success('Synchronisation Shopify réussie !');
                } catch (syncError) {
                    console.error('Sync error:', syncError);
                    toast.error('Erreur de synchronisation Shopify');
                }
            }
            
            const params = {};
            if (search) params.search = search;
            if (vendorFilter && vendorFilter !== 'all') params.vendor_id = vendorFilter;
            
            const response = await ordersAPI.getAll(params);
            const data = response.data;
            setAllOrders(data);
            
            // Calculate counts per status
            const counts = {
                nouvelle: data.filter(o => o.status === 'nouvelle').length,
                confirme: data.filter(o => o.status === 'confirme').length,
                en_attente: data.filter(o => o.status === 'en_attente').length,
                injoignable: data.filter(o => o.status === 'injoignable').length,
                rupture_stock: data.filter(o => o.status === 'rupture_stock').length,
                indisponible: data.filter(o => o.status === 'indisponible').length,
                expedie: data.filter(o => o.status === 'expedie').length,
                livre: data.filter(o => o.status === 'livre').length,
                annule: data.filter(o => o.status === 'annule').length,
                retourne: data.filter(o => o.status === 'retourne').length,
            };
            setStatusCounts(counts);
            
            // Filter by active tab and date
            let filtered = data.filter(o => o.status === activeTab);
            
            if (dateFilter) {
                filtered = filtered.filter(o => {
                    const orderDate = o.date_commande?.substring(0, 10);
                    return orderDate === dateFilter;
                });
            }
            
            if (deliveryServiceFilter && deliveryServiceFilter !== 'all') {
                filtered = filtered.filter(o => o.service_livraison === deliveryServiceFilter);
            }
            
            // Sort by order number (extract number, biggest first)
            filtered.sort((a, b) => {
                const numA = parseInt(a.n_commande?.replace(/\D/g, '') || '0');
                const numB = parseInt(b.n_commande?.replace(/\D/g, '') || '0');
                return numB - numA;
            });
            
            setOrders(filtered);
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    }, [search, activeTab, dateFilter, vendorFilter, deliveryServiceFilter, user?.role]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Fetch vendeurs, drivers and all users for assignment
    useEffect(() => {
        const fetchAssignees = async () => {
            if (!canAssign) return;
            try {
                const [vendeursRes, driversRes, usersRes] = await Promise.all([
                    vendeursAPI.getAll(),
                    driversAPI.getAll(),
                    usersListAPI.getAll()
                ]);
                setVendeurs(vendeursRes.data || []);
                setDrivers(driversRes.data || []);
                setAllUsers(usersRes.data || []);
            } catch (error) {
                console.log('Could not fetch users');
            }
        };
        fetchAssignees();
    }, [canAssign]);

    const handleOpenDialog = (order = null) => {
        if (order) {
            setEditingOrder(order);
            // Calculate correct prix_total when opening order (Prix + Livraison - Remise)
            const prix = parseFloat(order.prix) || 0;
            const frais = parseFloat(order.frais_livraison) || 0;
            const remise = parseFloat(order.remise) || 0;
            const correctTotal = prix + frais - remise;
            
            setFormData({
                ...order,
                products: order.products || [], // Ensure products array exists
                vendor_fees: order.vendor_fees || [], // Ensure fees arrays exist
                pa_fees: order.pa_fees || [],
                frais_livraison: order.frais_livraison || '', // Keep empty if 0
                remise: order.remise || '', // Keep empty if 0
                remise_code: order.remise_code || '',
                prix_total: correctTotal, // Recalculate to fix any wrong values
            });
        } else {
            setEditingOrder(null);
            setFormData({
                ...emptyOrder,
                n_commande: `PA#${Date.now().toString().slice(-4)}`,
                frais_livraison: '', // Start empty, not 0
                remise: '', // Start empty
            });
        }
        setProductSearch('');
        setProductSearchResults([]);
        setNewFeeType('vendor');
        setNewFeeAmount('');
        setNewFeeNote('');
        setShowAddProductForm(false); // Reset add product form state
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingOrder(null);
        setFormData(emptyOrder);
        setProductSearch('');
        setProductSearchResults([]);
        setNewFeeType('vendor');
        setNewFeeAmount('');
        setNewFeeNote('');
        setShowAddProductForm(false); // Reset add product form state
    };

    const handleFormChange = (field, value) => {
        setFormData((prev) => {
            const newData = { ...prev, [field]: value };
            
            // Auto-calculate prix_total when products change
            if (field === 'products') {
                const productsSubtotal = (value || []).reduce((sum, p) => {
                    const price = parseFloat(p.price) || 0;
                    const qty = parseInt(p.quantity) || 1;
                    return sum + (price * qty);
                }, 0);
                newData.prix = productsSubtotal;
                newData.prix_total = productsSubtotal + (parseFloat(prev.frais_livraison) || 0) - (parseFloat(prev.remise) || 0);
                // Update produit field for display
                if (value && value.length > 0) {
                    newData.produit = value.map(p => `${p.title} x${p.quantity}`).join(', ');
                }
            }
            // Auto-calculate prix_total for manual price changes
            else if (field === 'prix' || field === 'frais_livraison' || field === 'remise') {
                const prix = field === 'prix' ? (parseFloat(value) || 0) : (parseFloat(prev.prix) || 0);
                const frais = field === 'frais_livraison' ? (parseFloat(value) || 0) : (parseFloat(prev.frais_livraison) || 0);
                const remise = field === 'remise' ? (parseFloat(value) || 0) : (parseFloat(prev.remise) || 0);
                newData.prix_total = prix + frais - remise;
            }
            
            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const orderData = {
                ...formData,
                prix: parseFloat(formData.prix) || 0,
                prix_achat: parseFloat(formData.prix_achat) || 0,
                frais_livraison: parseFloat(formData.frais_livraison) || 0,
                remise: parseFloat(formData.remise) || 0,
                remise_code: formData.remise_code || '',
                prix_total: parseFloat(formData.prix_total) || 0,
                products: formData.products || [],
                vendor_fees: formData.vendor_fees || [],
                pa_fees: formData.pa_fees || [],
            };

            if (editingOrder) {
                await ordersAPI.update(editingOrder.id, orderData);
                toast.success('Commande mise à jour');
            } else {
                await ordersAPI.create(orderData);
                toast.success('Commande créée');
            }
            
            handleCloseDialog();
            fetchOrders();
        } catch (error) {
            console.error('Order save error:', error);
            const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await ordersAPI.update(orderId, { status: newStatus });
            toast.success('Statut mis à jour');
            fetchOrders();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDelete = async () => {
        if (!orderToDelete) return;
        
        try {
            await ordersAPI.delete(orderToDelete.id);
            toast.success('Commande supprimée');
            setDeleteDialogOpen(false);
            setOrderToDelete(null);
            fetchOrders();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Selection handlers
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedOrders(orders.map(o => o.id));
        } else {
            setSelectedOrders([]);
        }
    };

    const handleSelectOrder = (orderId, checked) => {
        if (checked) {
            setSelectedOrders(prev => [...prev, orderId]);
        } else {
            setSelectedOrders(prev => prev.filter(id => id !== orderId));
        }
    };

    // Toggle ready for pickup - for vendeurs
    const handleToggleReadyForPickup = async (orderId) => {
        try {
            const response = await ordersAPI.toggleReadyForPickup(orderId);
            toast.success(response.data.message);
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur';
            toast.error(message);
        }
    };

    const handleClaimOrder = async (orderId) => {
        try {
            const response = await ordersAPI.claim(orderId);
            toast.success(response.data.message);
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la prise de commande';
            toast.error(message);
        }
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportResult(null);

        try {
            const response = await ordersAPI.importFromFile(file);
            setImportResult(response.data);
            toast.success(`Import réussi ! ${response.data.imported} nouvelles commandes, ${response.data.skipped} ignorées (doublons)`);
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'import';
            toast.error(message);
            setImportResult({ error: message });
        } finally {
            setImporting(false);
            event.target.value = ''; // Reset file input
        }
    };

    const handleClaimForVendor = async () => {
        if (!selectedVendorForClaim || !orderToClaimFor) return;

        try {
            const response = await ordersAPI.claimForVendor(orderToClaimFor.id, selectedVendorForClaim);
            toast.success(response.data.message);
            setClaimForVendorDialogOpen(false);
            setOrderToClaimFor(null);
            setSelectedVendorForClaim('');
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'assignation';
            toast.error(message);
        }
    };

    const handleRejectOrder = async () => {
        if (!rejectReason || !orderToReject) return;

        setRejecting(true);
        try {
            const rejectData = {
                reason: rejectReason,
                suggested_price: suggestedPrice ? parseFloat(suggestedPrice) : null,
                notes: rejectNotes
            };
            
            const response = await ordersAPI.reject(orderToReject.id, rejectData);
            toast.success(response.data.message);
            setRejectDialogOpen(false);
            setOrderToReject(null);
            setRejectReason('');
            setSuggestedPrice('');
            setRejectNotes('');
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors du rejet';
            toast.error(message);
        } finally {
            setRejecting(false);
        }
    };

    const handleBulkStatusChange = async (newStatus) => {
        if (selectedOrders.length === 0) return;
        setBulkActionLoading(true);
        
        try {
            const response = await api.post('/orders/bulk/status', {
                order_ids: selectedOrders,
                status: newStatus
            });
            toast.success(response.data.message);
            setSelectedOrders([]);
            fetchOrders();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedOrders.length === 0) return;
        setBulkActionLoading(true);
        
        try {
            const response = await api.post('/orders/bulk/delete', {
                order_ids: selectedOrders
            });
            toast.success(response.data.message);
            setSelectedOrders([]);
            fetchOrders();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const isAllSelected = orders.length > 0 && selectedOrders.length === orders.length;
    const isSomeSelected = selectedOrders.length > 0 && selectedOrders.length < orders.length;

    // Send confirmation SMS to customer
    const handleSendConfirmationSms = async (order) => {
        if (sendingSms) return; // Prevent double click
        
        setSendingSms(order.id);
        try {
            const response = await api.post(`/orders/${order.id}/send-confirmation-sms`);
            toast.success(`SMS envoyé à ${order.phone}`, {
                description: `Lien de confirmation généré pour la commande ${order.n_commande}`
            });
            fetchOrders(); // Refresh to show updated status
        } catch (error) {
            console.error('SMS error:', error);
            toast.error(
                error.response?.data?.detail || 'Erreur lors de l\'envoi du SMS',
                { description: 'Vérifiez la configuration SMS dans les paramètres' }
            );
        } finally {
            setSendingSms(null);
        }
    };

    // Manually confirm order (admin: when customer calls/emails directly)
    const handleConfirmManually = async (order) => {
        if (confirmingManually) return;
        
        if (!window.confirm(`Confirmer manuellement la commande ${order.n_commande} ?\n\nUtilisez cette option si le client vous a contacté directement (appel ou email).\n\nLes vendeurs seront notifiés par Telegram.`)) {
            return;
        }
        
        setConfirmingManually(order.id);
        try {
            const response = await api.post(`/orders/${order.id}/confirm-manual`);
            toast.success(response.data.message, {
                description: 'Les vendeurs ont été notifiés par Telegram'
            });
            fetchOrders();
        } catch (error) {
            console.error('Manual confirm error:', error);
            toast.error(
                error.response?.data?.detail || 'Erreur lors de la confirmation',
            );
        } finally {
            setConfirmingManually(null);
        }
    };

    // Mark order as injoignable (unreachable client)
    const handleMarkInjoignable = async (order) => {
        if (markingInjoignable) return;
        
        if (!window.confirm(`Marquer la commande ${order.n_commande} comme "Client Injoignable" ?\n\n📵 Le client recevra un SMS de rappel toutes les 24h pendant 3 jours.\n\n⚠️ Si aucune réponse après 3 jours, la commande sera automatiquement annulée.`)) {
            return;
        }
        
        setMarkingInjoignable(order.id);
        try {
            const response = await api.post(`/orders/${order.id}/mark-injoignable`);
            toast.success('Client marqué comme injoignable', {
                description: response.data.message
            });
            fetchOrders();
        } catch (error) {
            console.error('Mark injoignable error:', error);
            toast.error(
                error.response?.data?.detail || 'Erreur lors du marquage',
            );
        } finally {
            setMarkingInjoignable(null);
        }
    };

    // Send rupture de stock SMS manually
    const handleSendRuptureSms = async (order) => {
        if (sendingRuptureSms) return;
        
        if (!window.confirm(`Envoyer un SMS au client pour l'informer que sa commande ${order.n_commande} est en rupture de stock ?\n\n💰 Coût: ~$0.20`)) {
            return;
        }
        
        setSendingRuptureSms(order.id);
        try {
            const response = await api.post(`/orders/${order.id}/sms-rupture-stock`);
            toast.success('SMS rupture de stock envoyé', {
                description: response.data.message
            });
            fetchOrders();
        } catch (error) {
            console.error('Send rupture SMS error:', error);
            toast.error(
                error.response?.data?.detail || 'Erreur lors de l\'envoi du SMS',
            );
        } finally {
            setSendingRuptureSms(null);
        }
    };

    // Fetch client reward points by phone
    const fetchClientPoints = async (phone) => {
        if (!phone || phone.length < 9) {
            setClientPoints(null);
            return;
        }
        
        setLoadingPoints(true);
        try {
            const response = await api.get(`/clients/phone/${phone}/points`);
            setClientPoints(response.data);
            setPointsToUse(0); // Reset points to use when client changes
        } catch (error) {
            console.error('Error fetching client points:', error);
            setClientPoints(null);
        } finally {
            setLoadingPoints(false);
        }
    };

    // Apply points as discount
    const handleApplyPoints = () => {
        if (!clientPoints || pointsToUse <= 0) return;
        
        const maxPoints = clientPoints.points_balance;
        const orderTotal = parseFloat(formData.prix_total) || 0;
        
        // Limit points to max available or order total
        const effectivePoints = Math.min(pointsToUse, maxPoints, orderTotal);
        
        if (effectivePoints <= 0) {
            toast.error('Points invalides');
            return;
        }
        
        // Update prix_total with discount
        const newTotal = orderTotal - effectivePoints;
        setFormData(prev => ({
            ...prev,
            prix_total: newTotal,
            points_used: effectivePoints,
            points_discount: effectivePoints
        }));
        
        toast.success(`${effectivePoints} points appliqués (-${effectivePoints} DA)`);
    };

    // Remove points discount
    const handleRemovePoints = () => {
        const currentDiscount = formData.points_discount || 0;
        if (currentDiscount <= 0) return;
        
        const newTotal = (parseFloat(formData.prix_total) || 0) + currentDiscount;
        setFormData(prev => ({
            ...prev,
            prix_total: newTotal,
            points_used: 0,
            points_discount: 0
        }));
        
        setPointsToUse(0);
        toast.success('Points retirés');
    };

    // Block client for fraud
    const handleBlockClient = async (phone, reason, orderId) => {
        try {
            await api.post('/clients/block', {
                phone: phone,
                reason: reason,
                order_id: orderId
            });
            toast.success(`Client ${phone} bloqué`);
            // Refresh to show updated status
            if (viewingOrder) {
                handleViewOrder(viewingOrder);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du blocage');
        }
    };

    const handleUnblockClient = async (phone) => {
        try {
            await api.post(`/clients/unblock/${encodeURIComponent(phone)}`);
            toast.success(`Client ${phone} débloqué`);
            // Refresh orders to update blocked status
            fetchOrders();
            // Refresh fraud info in modal
            if (viewingOrder) {
                handleViewOrder(viewingOrder);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du déblocage');
        }
    };

    // Fetch fraud info when viewing order
    const handleViewOrder = async (order) => {
        setViewingOrder(order);
        setViewOrderOpen(true);
        
        // Fetch fraud check for admin
        if (user?.role === 'admin' || user?.role === 'super_admin') {
            try {
                const response = await api.get(`/orders/${order.id}/fraud-check`);
                setViewingOrder(prev => ({
                    ...prev,
                    fraud_info: response.data
                }));
            } catch (error) {
                console.error('Failed to fetch fraud info:', error);
            }
        }
    };

    return (
        <Layout>
            <div className="space-y-6 overflow-x-hidden max-w-full" data-testid="orders-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Commandes</h1>
                        <p className="text-muted-foreground mt-1 text-sm md:text-base">
                            Gérez vos commandes de parfums
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchOrders(false)}
                            disabled={loading}
                            data-testid="refresh-btn"
                            className="border-primary text-primary hover:bg-primary/10"
                        >
                            <RefreshCw className={cn('w-4 h-4 md:mr-2', loading && 'animate-spin')} />
                            <span className="hidden md:inline">Actualiser</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExportCSV}
                            disabled={!orders.length}
                            data-testid="export-csv-btn"
                            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                        >
                            <Download className="w-4 h-4 md:mr-2" />
                            <span className="hidden md:inline">Export CSV</span>
                        </Button>
                        {(user?.role === 'admin' || user?.role === 'super_admin') && (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => document.getElementById('file-import').click()}
                                    disabled={importing}
                                    className="border-green-500 text-green-600 hover:bg-green-50"
                                >
                                    {importing ? (
                                        <Loader2 className="w-4 h-4 md:mr-2 animate-spin" />
                                    ) : (
                                        <Upload className="w-4 h-4 md:mr-2" />
                                    )}
                                    <span className="hidden md:inline">Import CSV</span>
                                </Button>
                                <input
                                    id="file-import"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleImportFile}
                                    style={{ display: 'none' }}
                                />
                            </>
                        )}
                        {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur') && (
                            <Button
                                className="btn-luxury"
                                size="sm"
                                onClick={() => handleOpenDialog()}
                                data-testid="new-order-btn"
                            >
                                <Plus className="w-4 h-4 md:mr-2" />
                                <span className="hidden md:inline">Nouvelle Commande</span>
                            </Button>
                        )}
                    </div>
                </div>

                {/* Search & Date Filter & Vendor Filter - Mobile Optimized */}
                <Card className="card-luxury">
                    <CardContent className="p-4 md:p-4">
                        <div className="flex flex-col gap-4">
                            {/* Search - Full width, larger on mobile */}
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-12 h-14 md:h-10 text-lg md:text-base input-luxury"
                                    data-testid="search-input"
                                />
                            </div>
                            
                            {/* Filters row - Stack on mobile */}
                            <div className="flex flex-col md:flex-row gap-3">
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="date"
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value)}
                                        className="input-luxury h-12 md:h-10 text-base md:text-sm flex-1 md:w-auto"
                                        data-testid="date-filter"
                                    />
                                    {dateFilter && (
                                        <Button 
                                            variant="ghost" 
                                            size="lg"
                                            onClick={() => setDateFilter('')}
                                            className="text-muted-foreground hover:text-foreground h-12 w-12 md:h-10 md:w-10"
                                        >
                                            <X className="w-5 h-5" />
                                        </Button>
                                    )}
                                </div>
                                {/* Vendor Filter - Admin only */}
                                {canAssign && vendeurs.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <Select value={vendorFilter} onValueChange={setVendorFilter}>
                                            <SelectTrigger className="h-12 md:h-10 text-base md:text-sm flex-1 md:w-[180px]">
                                                <SelectValue placeholder="Filtrer par vendeur" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all" className="text-base">👥 Tous les vendeurs</SelectItem>
                                                {vendeurs.map((v) => (
                                                    <SelectItem key={v.id} value={v.id} className="text-base">
                                                        {v.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {vendorFilter !== 'all' && (
                                            <Button 
                                                variant="ghost" 
                                                size="lg"
                                                onClick={() => setVendorFilter('all')}
                                                className="text-muted-foreground hover:text-foreground h-12 w-12 md:h-10 md:w-10"
                                            >
                                                <X className="w-5 h-5" />
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {/* Delivery Service Filter */}
                                <div className="flex items-center gap-2">
                                    <Select value={deliveryServiceFilter} onValueChange={setDeliveryServiceFilter}>
                                        <SelectTrigger className="h-12 md:h-10 text-base md:text-sm flex-1 md:w-[180px]" data-testid="delivery-service-filter">
                                            <SelectValue placeholder="Service livraison" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all" className="text-base">🚚 Tous les services</SelectItem>
                                            <SelectItem value="zr_express" className="text-base">ZR Express</SelectItem>
                                            <SelectItem value="yalidine" className="text-base">Yalidine</SelectItem>
                                            <SelectItem value="dhd" className="text-base">DHD</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {deliveryServiceFilter !== 'all' && (
                                        <Button 
                                            variant="ghost" 
                                            size="lg"
                                            onClick={() => setDeliveryServiceFilter('all')}
                                            className="text-muted-foreground hover:text-foreground h-12 w-12 md:h-10 md:w-10"
                                        >
                                            <X className="w-5 h-5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Status Tabs - Clean Light Background */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full flex flex-nowrap h-auto gap-2 bg-slate-50 border border-slate-200 p-3 rounded-xl overflow-x-auto hide-scrollbar shadow-sm">
                        {statusTabs.map((tab) => {
                            const count = statusCounts[tab.value] || 0;
                            const isActive = activeTab === tab.value;
                            // Improved colors with better contrast
                            const tabStyles = {
                                nouvelle: { 
                                    bg: isActive ? '#2563eb' : '#dbeafe', 
                                    border: '#93c5fd', 
                                    text: isActive ? '#ffffff' : '#1d4ed8',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#3b82f6',
                                    countText: '#ffffff'
                                },
                                confirme: { 
                                    bg: isActive ? '#0891b2' : '#cffafe', 
                                    border: '#67e8f9', 
                                    text: isActive ? '#ffffff' : '#0e7490',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#06b6d4',
                                    countText: '#ffffff'
                                },
                                en_attente: { 
                                    bg: isActive ? '#d97706' : '#fef3c7', 
                                    border: '#fcd34d', 
                                    text: isActive ? '#ffffff' : '#b45309',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#f59e0b',
                                    countText: '#ffffff'
                                },
                                injoignable: { 
                                    bg: isActive ? '#dc2626' : '#fecaca', 
                                    border: '#f87171', 
                                    text: isActive ? '#ffffff' : '#b91c1c',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#ef4444',
                                    countText: '#ffffff'
                                },
                                rupture_stock: { 
                                    bg: isActive ? '#be123c' : '#fda4af', 
                                    border: '#fb7185', 
                                    text: isActive ? '#ffffff' : '#9f1239',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#f43f5e',
                                    countText: '#ffffff'
                                },
                                indisponible: { 
                                    bg: isActive ? '#334155' : '#cbd5e1', 
                                    border: '#94a3b8', 
                                    text: isActive ? '#ffffff' : '#1e293b',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#64748b',
                                    countText: '#ffffff'
                                },
                                expedie: { 
                                    bg: isActive ? '#7c3aed' : '#ddd6fe', 
                                    border: '#a78bfa', 
                                    text: isActive ? '#ffffff' : '#6d28d9',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#8b5cf6',
                                    countText: '#ffffff'
                                },
                                livre: { 
                                    bg: isActive ? '#16a34a' : '#bbf7d0', 
                                    border: '#86efac', 
                                    text: isActive ? '#ffffff' : '#15803d',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#22c55e',
                                    countText: '#ffffff'
                                },
                                annule: { 
                                    bg: isActive ? '#dc2626' : '#fecaca', 
                                    border: '#f87171', 
                                    text: isActive ? '#ffffff' : '#b91c1c',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#ef4444',
                                    countText: '#ffffff'
                                },
                                retourne: { 
                                    bg: isActive ? '#ea580c' : '#fed7aa', 
                                    border: '#fdba74', 
                                    text: isActive ? '#ffffff' : '#c2410c',
                                    countBg: isActive ? 'rgba(255,255,255,0.25)' : '#f97316',
                                    countText: '#ffffff'
                                },
                            };
                            const style = tabStyles[tab.value] || tabStyles['annule']; // fallback
                            return (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all min-w-[80px] data-[state=active]:shadow-lg"
                                    style={{
                                        backgroundColor: style.bg,
                                        borderColor: isActive ? 'transparent' : style.border,
                                        borderWidth: isActive ? '0' : '2px',
                                        borderStyle: 'solid'
                                    }}
                                    data-testid={`tab-${tab.value}`}
                                >
                                    <span className="text-xl">{getStatusIcon(tab.value)}</span>
                                    <span 
                                        className="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
                                        style={{ color: style.text }}
                                    >
                                        {tab.label.length > 10 ? tab.label.slice(0, 8) + '.' : tab.label}
                                    </span>
                                    <span 
                                        className="text-sm font-bold px-2 py-0.5 rounded-full min-w-[28px] text-center"
                                        style={{ 
                                            backgroundColor: style.countBg,
                                            color: style.countText
                                        }}
                                    >
                                        {count}
                                    </span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </Tabs>

                {/* Bulk Actions Bar */}
                {selectedOrders.length > 0 && (
                    <Card className="card-luxury bg-primary/5 border-primary/20">
                        <CardContent className="p-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <CheckSquare className="w-5 h-5 text-primary" />
                                    <span className="font-medium">{selectedOrders.length} sélectionnée(s)</span>
                                </div>
                                <div className="h-6 w-px bg-border" />
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('en_attente')}
                                        disabled={bulkActionLoading}
                                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                    >
                                        <Clock className="w-4 h-4 mr-1" />
                                        En Attente
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('confirme')}
                                        disabled={bulkActionLoading}
                                        className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        Confirmer
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('expedie')}
                                        disabled={bulkActionLoading}
                                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    >
                                        <Truck className="w-4 h-4 mr-1" />
                                        Expédier
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('livre')}
                                        disabled={bulkActionLoading}
                                        className="text-green-600 border-green-200 hover:bg-green-50"
                                    >
                                        <Package className="w-4 h-4 mr-1" />
                                        Livré
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('rupture_stock')}
                                        disabled={bulkActionLoading}
                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                    >
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Rupture
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('annule')}
                                        disabled={bulkActionLoading}
                                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                    >
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Annuler
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkStatusChange('retourne')}
                                        disabled={bulkActionLoading}
                                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                    >
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Retourner
                                    </Button>
                                    <div className="h-6 w-px bg-border" />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={handleBulkDelete}
                                        disabled={bulkActionLoading}
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Supprimer
                                    </Button>
                                </div>
                                <div className="ml-auto">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setSelectedOrders([])}
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Annuler
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Orders Table/Cards */}
                <Card className="card-luxury !p-0 md:p-6 overflow-hidden">
                    <CardContent className="p-0">
                        {/* Mobile Compact Card View */}
                        <div className="block md:hidden p-2 max-w-full">
                            <div className="grid grid-cols-1 gap-2">
                                {orders.map((order) => (
                                    <div 
                                        key={order.id} 
                                        className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden w-full"
                                        data-testid={`mobile-order-card-${order.id}`}
                                    >
                                        {/* Card Header: Status Dropdown + Price */}
                                        <div className={cn(
                                            "px-3 py-2 flex items-center justify-between w-full",
                                            order.status === 'nouvelle' && "bg-blue-500",
                                            order.status === 'confirme' && "bg-cyan-500",
                                            order.status === 'en_attente' && "bg-amber-500",
                                            order.status === 'injoignable' && "bg-rose-500",
                                            order.status === 'rupture_stock' && "bg-red-600",
                                            order.status === 'indisponible' && "bg-slate-700",
                                            order.status === 'expedie' && "bg-purple-500",
                                            order.status === 'livre' && "bg-green-500",
                                            order.status === 'annule' && "bg-gray-500",
                                            order.status === 'retourne' && "bg-orange-500",
                                        )}>
                                            {/* Status Dropdown */}
                                            <Select 
                                                value={order.status} 
                                                onValueChange={(newStatus) => handleStatusChange(order.id, newStatus)}
                                            >
                                                <SelectTrigger className="w-auto h-7 bg-white/20 border-white/30 text-white font-semibold text-sm px-2 py-0">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {statusOptions.map((opt) => (
                                                        <SelectItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <span className="text-white font-bold text-lg">{formatCurrency(order.prix_total)}</span>
                                        </div>
                                        
                                        {/* Card Body */}
                                        <div className="p-3">
                                            {/* Row 1: Order # */}
                                            <p className="text-xs text-slate-500 mb-1">{order.n_commande}</p>
                                            
                                            {/* Row 2: Product Name - Copyable */}
                                            <div className="flex items-center gap-1 mb-2 group/mobile-product">
                                                <p className="text-sm text-slate-700 leading-tight cursor-pointer hover:text-primary"
                                                   onClick={() => handleCopyProduct(order.produit, `mobile-${order.id}`)}
                                                   data-testid={`copy-product-mobile-${order.id}`}>
                                                    {order.produit}
                                                </p>
                                                <button 
                                                    onClick={() => handleCopyProduct(order.produit, `mobile-${order.id}`)}
                                                    className="opacity-0 group-hover/mobile-product:opacity-100 transition-opacity p-0.5">
                                                    {copiedId === `mobile-${order.id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
                                                </button>
                                            </div>
                                            
                                            {/* Row 3: Customer Name */}
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-bold text-lg text-slate-900">{order.full_name}</p>
                                                {order.is_blocked_client && (
                                                    <Badge 
                                                        className="bg-red-600 text-white text-xs px-1.5 py-0 cursor-pointer hover:bg-red-700"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (window.confirm(`Voulez-vous débloquer le client ${order.phone} ?`)) {
                                                                handleUnblockClient(order.phone);
                                                            }
                                                        }}
                                                        title="Cliquez pour débloquer"
                                                    >
                                                        ⛔ BLOQUÉ
                                                    </Badge>
                                                )}
                                                {order.is_mauvais_client && !order.is_blocked_client && (
                                                    <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-1.5 py-0">
                                                        <AlertTriangle className="w-3 h-3 mr-0.5" />
                                                        Mauvais
                                                    </Badge>
                                                )}
                                            </div>
                                            
                                            {/* Row 4: Address */}
                                            <p className="text-sm text-slate-500 mb-2">{order.adresse}</p>
                                            
                                            {/* Row 5: Vendeur Tag (if assigned) */}
                                            {(order.assigned_vendeur_name || order.claimed_by_vendeur_name) && (
                                                <div className="mb-2">
                                                    <span className="inline-flex items-center px-2.5 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                                                        👤 {order.assigned_vendeur_name || order.claimed_by_vendeur_name}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {/* Row 6: Suggested Vendors (if any and no assigned vendor) */}
                                            {order.suggested_vendors && order.suggested_vendors.length > 0 && !order.assigned_vendeur_id && (
                                                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                                    <p className="text-xs font-medium text-amber-700 mb-1">💡 Vendeurs suggérés:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {order.suggested_vendors.slice(0, 3).map((vendor, idx) => (
                                                            <span 
                                                                key={idx}
                                                                className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full"
                                                            >
                                                                {vendor.vendeur_name} ({new Intl.NumberFormat('fr-DZ').format(vendor.prix)} DA)
                                                            </span>
                                                        ))}
                                                        {order.suggested_vendors.length > 3 && (
                                                            <span className="text-xs text-amber-600">+{order.suggested_vendors.length - 3} autres</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Row 7: Actions - Call + Confirm/Edit */}
                                            <div className="flex items-center gap-2">
                                                {/* Call Button */}
                                                <a 
                                                    href={`tel:${order.phone}`}
                                                    className="flex items-center justify-center gap-1.5 bg-green-500 text-white rounded-lg px-3 py-2 font-semibold text-sm active:bg-green-600 shadow-sm flex-1 min-w-0"
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-testid={`call-btn-${order.id}`}
                                                >
                                                    <Phone className="w-4 h-4 flex-shrink-0" />
                                                    <span className="truncate">{order.phone}</span>
                                                </a>
                                                
                                                {/* Confirm Button (if applicable) */}
                                                {order.status === 'nouvelle' && order.confirmation_status !== 'confirmed' && (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur' || user?.role === 'office_agent') && (
                                                    <Button
                                                        size="sm"
                                                        className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 h-auto flex-shrink-0"
                                                        onClick={() => handleConfirmManually(order)}
                                                        disabled={confirmingManually === order.id}
                                                        data-testid={`confirm-btn-${order.id}`}
                                                    >
                                                        {confirmingManually === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                                
                                                {/* Edit Button */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleOpenDialog(order)}
                                                    className="px-3 py-2 h-auto flex-shrink-0"
                                                    data-testid={`edit-btn-${order.id}`}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {orders.length === 0 && (
                                    <div className="p-6 text-center text-muted-foreground bg-white rounded-lg">
                                        Aucune commande trouvée
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Desktop Table View */}
                        <div className="hidden md:block table-container">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="table-header w-12">
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={isSomeSelected}
                                                onCheckedChange={handleSelectAll}
                                                data-testid="select-all-checkbox"
                                            />
                                        </TableHead>
                                        {/* Fraud Alert Column - Admin/Confirmateur only */}
                                        {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur') && (
                                            <TableHead className="table-header w-10 text-center" title="Alerte Fraude">⚠️</TableHead>
                                        )}
                                        <TableHead className="table-header">N° Commande</TableHead>
                                        <TableHead className="table-header">Date</TableHead>
                                        <TableHead className="table-header">Client</TableHead>
                                        <TableHead className="table-header">Produits</TableHead>
                                        <TableHead className="table-header text-center">URL Produit</TableHead>
                                        <TableHead className="table-header text-right">Prix</TableHead>
                                        <TableHead className="table-header text-right">Livraison</TableHead>
                                        <TableHead className="table-header text-right">Total</TableHead>
                                        <TableHead className="table-header">Statut</TableHead>
                                        <TableHead className="table-header">Vendeur / Livreur</TableHead>
                                        <TableHead className="table-header w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={13} className="text-center py-12">
                                                <div className="flex justify-center">
                                                    <div className="spinner" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : orders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={13} className="text-center py-12 text-muted-foreground">
                                                Aucune commande trouvée
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        orders.map((order) => (
                                            <TableRow 
                                                key={order.id} 
                                                className={cn("table-row", selectedOrders.includes(order.id) && "bg-primary/5")} 
                                                data-testid={`order-row-${order.id}`}
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedOrders.includes(order.id)}
                                                        onCheckedChange={(checked) => handleSelectOrder(order.id, checked)}
                                                        data-testid={`select-order-${order.id}`}
                                                    />
                                                </TableCell>
                                                {/* Fraud Alert Cell - Admin/Confirmateur only */}
                                                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur') && (
                                                    <TableCell className="text-center">
                                                        {order.client_ip && (
                                                            <span 
                                                                className="cursor-pointer text-lg" 
                                                                title={`IP: ${order.client_ip}`}
                                                                onClick={() => handleViewOrder(order)}
                                                            >
                                                                🌐
                                                            </span>
                                                        )}
                                                        {order.is_blocked_client && (
                                                            <span className="text-lg" title="Client bloqué">⛔</span>
                                                        )}
                                                        {order.is_suspicious_ip && (
                                                            <span className="text-lg text-red-500" title="IP suspecte - même IP avec identité différente">🚨</span>
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <span 
                                                        className="order-number font-medium cursor-pointer hover:text-primary hover:underline"
                                                        onClick={() => handleViewOrder(order)}
                                                    >
                                                        {order.n_commande}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">
                                                        {formatDate(order.date_commande)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium">{order.full_name}</p>
                                                            {order.is_blocked_client && (
                                                                <Badge 
                                                                    className="bg-red-600 text-white text-xs px-1.5 py-0 cursor-pointer hover:bg-red-700"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm(`Voulez-vous débloquer le client ${order.phone} ?`)) {
                                                                            handleUnblockClient(order.phone);
                                                                        }
                                                                    }}
                                                                    title="Cliquez pour débloquer"
                                                                >
                                                                    ⛔ BLOQUÉ
                                                                </Badge>
                                                            )}
                                                            {order.is_mauvais_client && !order.is_blocked_client && (
                                                                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-1.5 py-0">
                                                                    <AlertTriangle className="w-3 h-3 mr-0.5" />
                                                                    Mauvais
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{order.phone}</p>
                                                        <p className="text-xs text-muted-foreground truncate max-w-[180px]" title={order.adresse}>
                                                            📍 {order.adresse || '-'}
                                                        </p>
                                                        {order.observation && (
                                                            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                                                                <StickyNote className="w-3 h-3" />
                                                                {order.observation.length > 30 ? order.observation.substring(0, 30) + '...' : order.observation}
                                                            </p>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {/* Products column - names and quantities */}
                                                    <div className="space-y-1 min-w-[180px]">
                                                        {order.products && order.products.length > 0 ? (
                                                            order.products.map((product, idx) => (
                                                                <div key={idx} className="text-sm border-b border-dashed last:border-0 pb-1 last:pb-0 group/product">
                                                                    <div className="flex items-center gap-1">
                                                                        <p className="font-medium truncate max-w-[180px] cursor-pointer hover:text-primary" 
                                                                           title={`Cliquer pour copier: ${product.title}`}
                                                                           onClick={() => handleCopyProduct(product.title, `${order.id}-${idx}`)}
                                                                           data-testid={`copy-product-${order.id}-${idx}`}>
                                                                            {product.title}
                                                                        </p>
                                                                        <button 
                                                                            onClick={() => handleCopyProduct(product.title, `${order.id}-${idx}`)}
                                                                            className="opacity-0 group-hover/product:opacity-100 transition-opacity p-0.5 hover:bg-muted rounded"
                                                                            data-testid={`copy-btn-${order.id}-${idx}`}>
                                                                            {copiedId === `${order.id}-${idx}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Qté: {product.quantity}
                                                                    </p>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-sm group/product flex items-center gap-1">
                                                                <p className="font-medium truncate max-w-[180px] cursor-pointer hover:text-primary" 
                                                                   title={`Cliquer pour copier: ${order.produit}`}
                                                                   onClick={() => handleCopyProduct(order.produit, order.id)}
                                                                   data-testid={`copy-product-${order.id}`}>
                                                                    {order.produit || '-'}
                                                                </p>
                                                                {order.produit && (
                                                                    <button 
                                                                        onClick={() => handleCopyProduct(order.produit, order.id)}
                                                                        className="opacity-0 group-hover/product:opacity-100 transition-opacity p-0.5 hover:bg-muted rounded"
                                                                        data-testid={`copy-btn-${order.id}`}>
                                                                        {copiedId === order.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                {/* URL Produit column */}
                                                <TableCell className="text-center">
                                                    {order.produit_url ? (
                                                        <a
                                                            href={order.produit_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded hover:bg-primary/20 text-xs font-medium"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                            Voir
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {/* Price column - individual prices */}
                                                    <div className="space-y-1 min-w-[100px]">
                                                        {order.products && order.products.length > 0 ? (
                                                            order.products.map((product, idx) => (
                                                                <div key={idx} className="text-sm border-b border-dashed last:border-0 pb-1 last:pb-0">
                                                                    <p className="font-semibold">
                                                                        {formatCurrency(product.price * product.quantity)}
                                                                    </p>
                                                                    {product.prix_achat > 0 && (
                                                                        <p className="text-xs text-green-600">
                                                                            Achat: {formatCurrency(product.prix_achat * product.quantity)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-sm">
                                                                <p className="font-semibold">{formatCurrency(order.prix)}</p>
                                                                {order.prix_achat > 0 && (
                                                                    <p className="text-xs text-green-600">
                                                                        Achat: {formatCurrency(order.prix_achat)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <span className="text-sm">
                                                        {formatCurrency(order.frais_livraison || 0)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <span className="font-bold text-lg text-primary">
                                                        {formatCurrency(order.prix_total)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {/* Status dropdown - Vendors can only change status AFTER claiming/assignment */}
                                                        {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur' ||
                                                          (user?.role === 'vendeur' && (order.claimed_by_vendeur_id === user?.id || order.assigned_vendeur_id === user?.id))) ? (
                                                            <Select
                                                                value={order.status}
                                                                onValueChange={(value) => handleStatusChange(order.id, value)}
                                                            >
                                                                <SelectTrigger className="w-36 h-9 text-xs" data-testid={`status-select-${order.id}`}>
                                                                    <Badge className={cn('status-badge px-2 py-1 text-xs font-semibold', getStatusColor(order.status))}>
                                                                        <span className="mr-1">{getStatusIcon(order.status)}</span>
                                                                        {getStatusLabel(order.status)}
                                                                    </Badge>
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {statusOptions.map((option) => (
                                                                        <SelectItem key={option.value} value={option.value}>
                                                                            <span className="mr-2">{getStatusIcon(option.value)}</span>
                                                                            {option.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <Badge className={cn('status-badge px-2 py-1 text-xs font-semibold', getStatusColor(order.status))}>
                                                                <span className="mr-1">{getStatusIcon(order.status)}</span>
                                                                {getStatusLabel(order.status)}
                                                            </Badge>
                                                        )}
                                                        {/* Confirmation traceability - Desktop */}
                                                        {order.confirmation_status === 'confirmed' && (
                                                            <Badge 
                                                                variant="outline" 
                                                                className={order.confirmed_by === 'client' 
                                                                    ? 'text-green-600 border-green-300 text-xs mt-1' 
                                                                    : 'text-purple-600 border-purple-300 text-xs mt-1'
                                                                }
                                                            >
                                                                {order.confirmed_by === 'client' 
                                                                    ? '✓ Par client' 
                                                                    : `✓ ${order.confirmed_by_user_name || 'Manuel'}`
                                                                }
                                                            </Badge>
                                                        )}
                                                        {/* Injoignable info badge */}
                                                        {order.status === 'injoignable' && (
                                                            <Badge 
                                                                variant="outline" 
                                                                className="text-rose-600 border-rose-300 text-xs mt-1"
                                                            >
                                                                📵 Rappel {order.injoignable_reminder_count || 0}/3
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm space-y-1">
                                                        {/* Vendeur */}
                                                        {order.claimed_by_vendeur_name || order.assigned_vendeur_name ? (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs">👤</span>
                                                                <p className="font-medium text-green-700">
                                                                    {order.claimed_by_vendeur_name || order.assigned_vendeur_name}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted-foreground text-xs">
                                                                - Pas de vendeur
                                                            </p>
                                                        )}
                                                        
                                                        {/* Delivery info */}
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-xs">🚚</span>
                                                            <p className="text-xs text-muted-foreground">
                                                                {order.type_livraison === 'bureau' ? 'Bureau' : 'Domicile'}
                                                                {order.service_livraison && ` · ${
                                                                    order.service_livraison === 'zr_express' ? 'ZR Express' :
                                                                    order.service_livraison === 'yalidine' ? 'Yalidine' :
                                                                    order.service_livraison === 'dhd' ? 'DHD' :
                                                                    order.service_livraison
                                                                }`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {/* Quick action buttons for new orders - visible on mobile */}
                                                        {order.status === 'nouvelle' && !order.claimed_by_vendeur_id && (
                                                            <>
                                                                {/* Boutons Prendre/Rejeter supprimés - Admin assigne les commandes */}
                                                            </>
                                                        )}
                                                        
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" data-testid={`order-menu-${order.id}`}>
                                                                <MoreVertical className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {/* Admin/Confirmateur can assign an unclaimed order to a vendor */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur') && 
                                                             !order.assigned_vendeur_id && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => {
                                                                        setOrderToClaimFor(order);
                                                                        setClaimForVendorDialogOpen(true);
                                                                    }}
                                                                    className="text-blue-600 font-semibold"
                                                                >
                                                                    🎯 Assigner à un vendeur
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Send confirmation SMS to customer */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur') && 
                                                             order.status === 'nouvelle' && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleSendConfirmationSms(order)}
                                                                    disabled={sendingSms === order.id}
                                                                    className="text-green-600"
                                                                >
                                                                    {sendingSms === order.id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                            Envoi en cours...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <MessageSquare className="w-4 h-4 mr-2" />
                                                                            📱 Envoyer SMS confirmation
                                                                        </>
                                                                    )}
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Manual confirmation (when customer calls/emails directly) */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur') && 
                                                             order.status === 'nouvelle' && 
                                                             order.confirmation_status !== 'confirmed' && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleConfirmManually(order)}
                                                                    disabled={confirmingManually === order.id}
                                                                    className="text-purple-600"
                                                                >
                                                                    {confirmingManually === order.id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                            Confirmation...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <CheckCircle className="w-4 h-4 mr-2" />
                                                                            ✅ Confirmer manuellement
                                                                        </>
                                                                    )}
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Mark as Injoignable - Admin, Confirmateur, Office Agent */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur' || user?.role === 'office_agent') && 
                                                             ['nouvelle', 'en_attente', 'confirme'].includes(order.status) && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleMarkInjoignable(order)}
                                                                    disabled={markingInjoignable === order.id}
                                                                    className="text-rose-600"
                                                                >
                                                                    {markingInjoignable === order.id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                            Marquage...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <PhoneOff className="w-4 h-4 mr-2" />
                                                                            📵 Client Injoignable
                                                                        </>
                                                                    )}
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Send Rupture de Stock SMS - Manual */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur') && 
                                                             order.status === 'rupture_stock' && !order.rupture_sms_sent && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleSendRuptureSms(order)}
                                                                    disabled={sendingRuptureSms === order.id}
                                                                    className="text-orange-600"
                                                                >
                                                                    {sendingRuptureSms === order.id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                            Envoi...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <MessageSquare className="w-4 h-4 mr-2" />
                                                                            📱 SMS Rupture de Stock
                                                                        </>
                                                                    )}
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Show if rupture SMS already sent */}
                                                            {order.status === 'rupture_stock' && order.rupture_sms_sent && (
                                                                <DropdownMenuItem disabled className="text-gray-400 text-xs">
                                                                    ✓ SMS rupture déjà envoyé
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Edit order - Admins always, Vendors only for their orders */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur' || 
                                                              (user?.role === 'vendeur' && (order.claimed_by_vendeur_id === user?.id || order.assigned_vendeur_id === user?.id))) && (
                                                                <DropdownMenuItem onClick={() => handleOpenDialog(order)}>
                                                                    <Edit className="w-4 h-4 mr-2" />
                                                                    Modifier
                                                                </DropdownMenuItem>
                                                            )}
                                                            
                                                            {/* Delete - ONLY Admin can delete orders */}
                                                            {(user?.role === 'admin' || user?.role === 'super_admin') && (
                                                                <DropdownMenuItem
                                                                    className="text-destructive"
                                                                    onClick={() => {
                                                                        setOrderToDelete(order);
                                                                        setDeleteDialogOpen(true);
                                                                    }}
                                                                >
                                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                                    Supprimer
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Order Dialog - Mobile Friendly */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingOrder ? 'Modifier la Commande' : 'Nouvelle Commande'}
                            </DialogTitle>
                        </DialogHeader>
                                
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label className="text-xs">N° Commande</Label>
                                    <Input
                                        value={formData.n_commande}
                                        onChange={(e) => handleFormChange('n_commande', e.target.value)}
                                        className="h-9"
                                        required
                                        data-testid="order-number-input"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Statut</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(value) => handleFormChange('status', value)}
                                    >
                                        <SelectTrigger className="h-9" data-testid="order-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {statusOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Products Section */}
                            <div className="space-y-3 p-3 border rounded-lg bg-muted/20">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-semibold flex items-center gap-2">
                                        <ShoppingBag className="w-4 h-4" />
                                        Produits
                                        {(formData.products || []).length > 0 && (
                                            <Badge variant="secondary">{(formData.products || []).length}</Badge>
                                        )}
                                    </Label>
                                </div>
                                
                                {/* BIG MANUAL ADD BUTTON - Simple button that shows form only when needed */}
                                <div className="p-2 border rounded-lg bg-white">
                                    {!showAddProductForm ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full h-10"
                                            onClick={() => setShowAddProductForm(true)}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ajouter un produit
                                        </Button>
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <p className="text-xs font-medium text-muted-foreground">Nouveau produit</p>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowAddProductForm(false)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <Input
                                                id="manual-product-name"
                                                placeholder="Nom du produit"
                                                className="h-10"
                                            />
                                            <div className="flex gap-2">
                                                <Input
                                                    id="manual-product-price"
                                                    type="number"
                                                    placeholder="Prix (DA)"
                                                    className="h-10 flex-1"
                                                />
                                                <Button
                                                    type="button"
                                                    className="h-10 px-4 bg-primary"
                                                    onClick={() => {
                                                        const nameInput = document.getElementById('manual-product-name');
                                                        const priceInput = document.getElementById('manual-product-price');
                                                        const name = nameInput?.value?.trim();
                                                        const price = parseFloat(priceInput?.value) || 0;
                                                        if (name) {
                                                            handleFormChange('products', [...(formData.products || []), {
                                                                id: `manual-${Date.now()}`,
                                                                title: name,
                                                                price: price,
                                                                quantity: 1,
                                                            }]);
                                                            nameInput.value = '';
                                                            priceInput.value = '';
                                                            setShowAddProductForm(false);
                                                        } else {
                                                            toast.error('Entrez le nom du produit');
                                                        }
                                                    }}
                                                >
                                                    <Plus className="w-4 h-4 mr-1" />
                                                    AJOUTER
                                                </Button>
                                            </div>
                                            
                                            {/* Shopify Search (optional) */}
                                            <div className="pt-2 border-t">
                                                <p className="text-xs text-muted-foreground mb-2">Ou rechercher dans le catalogue :</p>
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input
                                                        value={productSearch}
                                                        onChange={(e) => setProductSearch(e.target.value)}
                                                        placeholder="Rechercher..."
                                                        className="pl-8 h-9"
                                                        disabled={submitting}
                                                    />
                                                    {productSearchLoading && (
                                                        <Loader2 className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin" />
                                                    )}
                                                </div>
                                        </div>
                                        
                                        {/* Search Results */}
                                        {productSearchResults.length > 0 && (
                                            <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto bg-white">
                                                {productSearchResults.map((product) => (
                                                    <div
                                                        key={product.id}
                                                        className="flex items-center gap-2 p-2 hover:bg-primary/10 cursor-pointer border-b last:border-0"
                                                        onClick={() => {
                                                            handleAddProduct(product);
                                                            setProductSearch('');
                                                            setProductSearchResults([]);
                                                        }}
                                                    >
                                                        {/* Product Image in search results */}
                                                        {product.image_url ? (
                                                            <img 
                                                                src={product.image_url} 
                                                                alt={product.full_title || product.title}
                                                                className="w-10 h-10 object-cover rounded border"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
                                                                <ShoppingBag className="w-4 h-4 text-gray-400" />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium truncate">{product.full_title || product.title}</p>
                                                            <p className="text-xs text-primary font-bold">{formatCurrency(product.price)}</p>
                                                        </div>
                                                        <Button type="button" size="sm" variant="ghost" className="h-6 px-2">
                                                            <Plus className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {productSearch.length >= 2 && productSearchResults.length === 0 && !productSearchLoading && (
                                            <p className="text-xs text-muted-foreground text-center py-2">Aucun résultat</p>
                                        )}
                                    </div>
                                    )}
                                </div>
                                
                                {/* Selected Products List - With images */}
                                {(formData.products || []).length > 0 && (
                                    <div className="border rounded-lg divide-y bg-white">
                                        {(formData.products || []).map((product, index) => (
                                            <div key={product.id || index} className="p-3">
                                                <div className="flex items-center gap-3">
                                                    {/* Product Image */}
                                                    {product.image_url ? (
                                                        <img 
                                                            src={product.image_url} 
                                                            alt={product.title}
                                                            className="w-14 h-14 object-cover rounded-lg border"
                                                        />
                                                    ) : (
                                                        <div className="w-14 h-14 bg-gray-100 rounded-lg border flex items-center justify-center">
                                                            <ShoppingBag className="w-6 h-6 text-gray-400" />
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold truncate">{product.title}</p>
                                                        <p className="text-sm text-primary font-bold">
                                                            {formatCurrency(product.price)} × {product.quantity} = {formatCurrency((parseFloat(product.price) || 0) * (parseInt(product.quantity) || 1))}
                                                        </p>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-1">
                                                        <Button type="button" variant="outline" size="icon" className="h-7 w-7"
                                                            onClick={() => handleProductQuantityChange(index, -1)}>
                                                            <Minus className="w-3 h-3" />
                                                        </Button>
                                                        <span className="w-6 text-center text-sm font-medium">{product.quantity}</span>
                                                        <Button type="button" variant="outline" size="icon" className="h-7 w-7"
                                                            onClick={() => handleProductQuantityChange(index, 1)}>
                                                            <Plus className="w-3 h-3" />
                                                        </Button>
                                                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                                            onClick={() => handleRemoveProduct(index)}>
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-between p-3 bg-primary/10 text-sm font-bold">
                                            <span>Sous-total Produits</span>
                                            <span className="text-primary">{formatCurrency(productsTotal)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Client Info */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label className="text-xs">Nom Complet</Label>
                                    <Input
                                        value={formData.full_name}
                                        onChange={(e) => handleFormChange('full_name', e.target.value)}
                                        className="h-9"
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Téléphone</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => handleFormChange('phone', e.target.value)}
                                        className="h-9"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-1">
                                <Label className="text-xs">Adresse</Label>
                                <Input
                                    value={formData.adresse}
                                    onChange={(e) => handleFormChange('adresse', e.target.value)}
                                    className="h-9"
                                />
                            </div>

                            {/* Pricing Section - Complete with Prix d'Achat */}
                            <div className="p-4 bg-gray-50 rounded-lg space-y-4 border">
                                <p className="text-sm font-bold text-gray-700 border-b pb-2">💰 Détails des Prix</p>
                                
                                {/* Prix d'Achat - What vendor pays */}
                                <div className="space-y-1">
                                    <Label className="text-sm font-medium text-amber-700">Prix d'Achat (Coût)</Label>
                                    <Input
                                        type="number"
                                        value={formData.prix_achat || ''}
                                        onChange={(e) => handleFormChange('prix_achat', e.target.value)}
                                        placeholder="0"
                                        className="h-10 text-lg border-amber-300 focus:border-amber-500"
                                    />
                                    <p className="text-xs text-muted-foreground">Le prix que vous payez pour ce produit</p>
                                </div>
                                
                                {/* Prix de Vente + Livraison */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium">Prix de Vente</Label>
                                        <Input
                                            type="number"
                                            value={formData.prix}
                                            onChange={(e) => handleFormChange('prix', e.target.value)}
                                            className="h-10 text-lg font-semibold"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium">Livraison</Label>
                                        <Input
                                            type="number"
                                            value={formData.frais_livraison || ''}
                                            onChange={(e) => handleFormChange('frais_livraison', e.target.value)}
                                            placeholder="0"
                                            className="h-10 text-lg"
                                        />
                                    </div>
                                </div>
                                
                                {/* Remise / Coupon */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium text-green-700">Remise (DA)</Label>
                                        <Input
                                            type="number"
                                            value={formData.remise || ''}
                                            onChange={(e) => handleFormChange('remise', e.target.value)}
                                            placeholder="0"
                                            className="h-10 text-lg border-green-300 focus:border-green-500"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium text-green-700">Code Coupon</Label>
                                        <Input
                                            type="text"
                                            value={formData.remise_code || ''}
                                            onChange={(e) => handleFormChange('remise_code', e.target.value)}
                                            placeholder="Ex: VIP2026"
                                            className="h-10 border-green-300 focus:border-green-500"
                                        />
                                    </div>
                                </div>
                                
                                {/* Total Client */}
                                <div className="flex justify-between items-center pt-3 border-t-2 border-primary/30 bg-primary/5 -mx-4 px-4 py-3 rounded-b-lg">
                                    <span className="text-sm font-bold">TOTAL CLIENT (Prix + Livraison - Remise)</span>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            value={formData.prix_total}
                                            onChange={(e) => handleFormChange('prix_total', e.target.value)}
                                            className="h-10 w-32 text-lg font-bold text-right border-primary bg-white"
                                        />
                                        <span className="text-sm font-medium">DA</span>
                                    </div>
                                </div>
                                
                                {/* Reward Points Section */}
                                {formData.phone && (
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Gift className="w-5 h-5 text-amber-500" />
                                                <span className="font-semibold text-amber-800">Points de Fidélité</span>
                                            </div>
                                            {!clientPoints && (
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    onClick={() => fetchClientPoints(formData.phone)}
                                                    disabled={loadingPoints}
                                                    className="text-amber-600 border-amber-300"
                                                >
                                                    {loadingPoints ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        'Vérifier points'
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                        
                                        {clientPoints && (
                                            <>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-amber-700">
                                                        {clientPoints.client_exists ? clientPoints.client_name : 'Nouveau client'}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <Star className="w-4 h-4 text-amber-500" />
                                                        <span className="font-bold text-amber-800">
                                                            {clientPoints.points_balance} points
                                                        </span>
                                                        <span className="text-amber-600">
                                                            ({clientPoints.points_value_da} DA)
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {clientPoints.points_balance > 0 && !formData.points_used && (
                                                    <div className="flex items-center gap-2">
                                                        <Input
                                                            type="number"
                                                            placeholder="Points à utiliser"
                                                            value={pointsToUse}
                                                            onChange={(e) => setPointsToUse(Math.min(parseInt(e.target.value) || 0, clientPoints.points_balance))}
                                                            className="h-9 w-32"
                                                            max={clientPoints.points_balance}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            onClick={handleApplyPoints}
                                                            disabled={pointsToUse <= 0}
                                                            className="bg-amber-500 hover:bg-amber-600"
                                                        >
                                                            Appliquer
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setPointsToUse(clientPoints.points_balance)}
                                                            className="text-amber-600"
                                                        >
                                                            Max
                                                        </Button>
                                                    </div>
                                                )}
                                                
                                                {formData.points_used > 0 && (
                                                    <div className="flex items-center justify-between bg-green-100 p-2 rounded">
                                                        <span className="text-green-700 font-medium">
                                                            ✓ {formData.points_used} points utilisés (-{formData.points_discount} DA)
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={handleRemovePoints}
                                                            className="text-red-500 hover:text-red-700 h-7"
                                                        >
                                                            Retirer
                                                        </Button>
                                                    </div>
                                                )}
                                                
                                                {!clientPoints.client_exists && (
                                                    <p className="text-xs text-amber-600">
                                                        ℹ️ Nouveau client. Points seront crédités après livraison.
                                                    </p>
                                                )}
                                            </>
                                        )}
                                        
                                        <p className="text-xs text-amber-600">
                                            💡 100 points gagnés pour chaque 10,000 DA. 1 point = 1 DA de réduction.
                                        </p>
                                    </div>
                                )}
                                
                                {/* Profit Margin Display */}
                                {formData.prix_achat > 0 && formData.prix > 0 && (
                                    <div className="flex justify-between items-center text-sm pt-2 border-t">
                                        <span className="text-muted-foreground">Marge bénéficiaire</span>
                                        <span className={`font-bold ${(formData.prix - formData.prix_achat) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(formData.prix - formData.prix_achat)} 
                                            ({((formData.prix - formData.prix_achat) / formData.prix_achat * 100).toFixed(1)}%)
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Assignment - Admin only */}
                            {canAssign && (
                                <div className="space-y-3 p-2 bg-blue-50 rounded-lg">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Vendeur</Label>
                                            <Select
                                                value={formData.assigned_vendeur_id || 'none'}
                                                onValueChange={(value) => handleFormChange('assigned_vendeur_id', value === 'none' ? '' : value)}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue placeholder="Aucun" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Aucun</SelectItem>
                                                    {vendeurs.map((v) => (
                                                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Type de livraison</Label>
                                            <Select
                                                value={formData.type_livraison || 'domicile'}
                                                onValueChange={(value) => handleFormChange('type_livraison', value)}
                                            >
                                                <SelectTrigger className="h-9" data-testid="select-type-livraison">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {typeLivraisonOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Service de livraison</Label>
                                        <Select
                                            value={formData.service_livraison || 'none'}
                                            onValueChange={(value) => handleFormChange('service_livraison', value === 'none' ? '' : value)}
                                        >
                                            <SelectTrigger className="h-9" data-testid="select-service-livraison">
                                                <SelectValue placeholder="-- Sélectionner --" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {serviceLivraisonOptions.map((option) => (
                                                    <SelectItem key={option.value || 'none'} value={option.value || 'none'}>{option.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}

                            {/* Observation */}
                            <div className="space-y-1">
                                <Label className="text-xs">Observation</Label>
                                <Textarea
                                    value={formData.observation || ''}
                                    onChange={(e) => handleFormChange('observation', e.target.value)}
                                    placeholder="Notes..."
                                    rows={2}
                                    className="text-sm"
                                />
                            </div>

                            <DialogFooter className="gap-2">
                                <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 sm:flex-none">
                                    Annuler
                                </Button>
                                <Button type="submit" className="btn-luxury flex-1 sm:flex-none" disabled={submitting}>
                                    {submitting ? 'Enregistrement...' : 'Enregistrer'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Confirmer la suppression</DialogTitle>
                        </DialogHeader>
                        <p className="text-muted-foreground">
                            Êtes-vous sûr de vouloir supprimer la commande{' '}
                            <strong>{orderToDelete?.n_commande}</strong> ?
                        </p>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                                Supprimer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Order Detail View Dialog - Shopify Style */}
                <Dialog open={viewOrderOpen} onOpenChange={setViewOrderOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span>Résumé de la commande</span>
                                <Badge className={cn('status-badge', getStatusColor(viewingOrder?.status))}>
                                    {getStatusLabel(viewingOrder?.status)}
                                </Badge>
                            </DialogTitle>
                        </DialogHeader>
                        
                        {viewingOrder && (
                            <div className="space-y-4">
                                {/* Product Info */}
                                <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                                    <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400">
                                        <Package className="w-8 h-8" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">{viewingOrder.produit}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {formatCurrency(viewingOrder.prix)} × 1
                                        </p>
                                        {viewingOrder.produit_url && (
                                            <a href={viewingOrder.produit_url} target="_blank" rel="noopener noreferrer" 
                                               className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                                                <ExternalLink className="w-3 h-3" /> Voir le produit
                                            </a>
                                        )}
                                    </div>
                                    <div className="text-right font-semibold">
                                        {formatCurrency(viewingOrder.prix)}
                                    </div>
                                </div>

                                {/* Pricing Summary */}
                                <div className="space-y-2 border-t pt-4">
                                    <div className="flex justify-between text-sm">
                                        <span>Sous-total</span>
                                        <span>{formatCurrency(viewingOrder.prix)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-primary">Expédition (Livraison)</span>
                                        <span>{formatCurrency(viewingOrder.frais_livraison)}</span>
                                    </div>
                                    {viewingOrder.remise > 0 && (
                                        <div className="flex justify-between text-sm text-green-600">
                                            <span className="flex items-center gap-1">
                                                <Tag className="w-4 h-4" />
                                                Remise {viewingOrder.remise_code && `(${viewingOrder.remise_code})`}
                                            </span>
                                            <span>-{formatCurrency(viewingOrder.remise)}</span>
                                        </div>
                                    )}
                                    {viewingOrder.points_used > 0 && (
                                        <div className="flex justify-between text-sm text-amber-600">
                                            <span className="flex items-center gap-1">
                                                <Gift className="w-4 h-4" />
                                                Points utilisés ({viewingOrder.points_used})
                                            </span>
                                            <span>-{formatCurrency(viewingOrder.points_discount)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                                        <span>Total</span>
                                        <span>{formatCurrency(viewingOrder.prix_total)}</span>
                                    </div>
                                    {viewingOrder.points_earned > 0 && (
                                        <div className="flex justify-between text-sm text-green-600 bg-green-50 p-2 rounded">
                                            <span className="flex items-center gap-1">
                                                <Star className="w-4 h-4" />
                                                Points gagnés
                                            </span>
                                            <span className="font-semibold">+{viewingOrder.points_earned}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Payment & Delivery */}
                                <div className="space-y-3 border-t pt-4">
                                    <div>
                                        <p className="font-semibold text-sm">Méthode de paiement</p>
                                        <p className="text-sm text-muted-foreground">Paiement à la livraison</p>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm">Type de livraison</p>
                                        <p className="text-sm text-muted-foreground">
                                            {viewingOrder.type_livraison === 'bureau' ? '🏢 Bureau (Stop Desk)' : '🏠 À Domicile'}
                                        </p>
                                    </div>
                                    {viewingOrder.service_livraison && (
                                        <div>
                                            <p className="font-semibold text-sm">Service de livraison</p>
                                            <p className="text-sm text-muted-foreground">
                                                {viewingOrder.service_livraison === 'zr_express' ? 'ZR Express' :
                                                 viewingOrder.service_livraison === 'yalidine' ? 'Yalidine' :
                                                 viewingOrder.service_livraison === 'dhd' ? 'DHD' :
                                                 viewingOrder.service_livraison}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Shipping Address */}
                                <div className="border-t pt-4">
                                    <p className="font-semibold text-sm mb-2">Adresse d'expédition</p>
                                    <div className="text-sm space-y-1 text-muted-foreground">
                                        <p className="font-medium text-foreground">{viewingOrder.full_name}</p>
                                        <p>{viewingOrder.adresse}</p>
                                        <p>Algérie</p>
                                        <p className="font-medium text-foreground">{viewingOrder.phone}</p>
                                    </div>
                                </div>

                                {/* Client IP - Fraud Detection */}
                                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                                    <div className="border-t pt-4">
                                        <p className="font-semibold text-sm mb-2 flex items-center gap-2">
                                            🛡️ Détection Fraude
                                        </p>
                                        
                                        {viewingOrder.client_ip && (
                                            <div className="text-sm p-2 bg-gray-100 rounded font-mono mb-2">
                                                <p><strong>IP:</strong> {viewingOrder.client_ip}</p>
                                            </div>
                                        )}
                                        
                                        {/* Fraud Alerts */}
                                        {viewingOrder.fraud_info?.alerts?.length > 0 && (
                                            <div className="space-y-2 mb-3">
                                                {viewingOrder.fraud_info.alerts.map((alert, idx) => (
                                                    <div key={idx} className={`p-2 rounded text-sm ${
                                                        alert.severity === 'critical' ? 'bg-red-100 text-red-800 border border-red-300' :
                                                        alert.severity === 'high' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                                                        alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                                                        'bg-blue-50 text-blue-800 border border-blue-200'
                                                    }`}>
                                                        <div className="font-semibold">{alert.message}</div>
                                                        {alert.ip_address && (
                                                            <div className="text-xs mt-1 opacity-75">IP: {alert.ip_address}</div>
                                                        )}
                                                        {/* Show previous orders from same IP */}
                                                        {alert.previous_orders && alert.previous_orders.length > 0 && (
                                                            <div className="mt-2 pt-2 border-t border-current/20">
                                                                <div className="text-xs font-semibold mb-1">📋 Commandes précédentes (même IP):</div>
                                                                <div className="space-y-1">
                                                                    {alert.previous_orders.map((prevOrder, pIdx) => (
                                                                        <div key={pIdx} className="text-xs bg-white/50 p-1 rounded">
                                                                            <span className="font-mono font-semibold">{prevOrder.n_commande}</span>
                                                                            <span className="mx-1">|</span>
                                                                            <span>{prevOrder.full_name}</span>
                                                                            <span className="mx-1">|</span>
                                                                            <span>{prevOrder.phone}</span>
                                                                            <span className="mx-1">|</span>
                                                                            <span className={`font-semibold ${
                                                                                prevOrder.status === 'livre' ? 'text-green-700' :
                                                                                prevOrder.status === 'annule' ? 'text-red-700' :
                                                                                prevOrder.status === 'retourne' ? 'text-red-700' :
                                                                                ''
                                                                            }`}>{prevOrder.status}</span>
                                                                            <span className="mx-1">|</span>
                                                                            <span>{prevOrder.prix_total} DA</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Fraud Score */}
                                        {viewingOrder.fraud_info && (
                                            <div className={`p-2 rounded text-sm mb-3 ${
                                                viewingOrder.fraud_info.risk_level === 'critical' ? 'bg-red-500 text-white' :
                                                viewingOrder.fraud_info.risk_level === 'high' ? 'bg-orange-500 text-white' :
                                                viewingOrder.fraud_info.risk_level === 'medium' ? 'bg-yellow-500 text-white' :
                                                'bg-green-500 text-white'
                                            }`}>
                                                <strong>Score Fraude:</strong> {viewingOrder.fraud_info.fraud_score}/100 ({viewingOrder.fraud_info.risk_level})
                                            </div>
                                        )}
                                        
                                        {/* Block/Unblock Button */}
                                        {!viewingOrder.fraud_info?.is_blocked ? (
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="w-full"
                                                onClick={() => {
                                                    if (window.confirm('Voulez-vous bloquer ce client ?')) {
                                                        const reason = window.prompt('Raison du blocage (optionnel, laissez vide si pas de raison):') || '';
                                                        handleBlockClient(viewingOrder.phone, reason, viewingOrder.id);
                                                    }
                                                }}
                                            >
                                                ⛔ Bloquer ce client
                                            </Button>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="p-3 bg-red-100 border border-red-300 rounded text-red-800 text-center">
                                                    <strong>⛔ CLIENT BLOQUÉ</strong>
                                                    <p className="text-sm">{viewingOrder.fraud_info.block_reason}</p>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                                                    onClick={() => {
                                                        if (window.confirm('Voulez-vous débloquer ce client ?')) {
                                                            handleUnblockClient(viewingOrder.phone);
                                                        }
                                                    }}
                                                >
                                                    ✅ Débloquer ce client
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Observation */}
                                {viewingOrder.observation && (
                                    <div className="border-t pt-4">
                                        <p className="font-semibold text-sm mb-2">Observation / Note</p>
                                        <p className="text-sm p-3 bg-amber-50 rounded border border-amber-200 text-amber-800">
                                            {viewingOrder.observation}
                                        </p>
                                    </div>
                                )}

                                {/* Mauvais Client Warning */}
                                {viewingOrder.is_mauvais_client && (
                                    <div className="p-3 bg-red-50 rounded border border-red-200 flex items-center gap-2">
                                        <AlertTriangle className="w-5 h-5 text-red-600" />
                                        <div>
                                            <p className="font-semibold text-red-700">Mauvais Client</p>
                                            <p className="text-sm text-red-600">Paiement à l'avance requis</p>
                                        </div>
                                    </div>
                                )}

                                {/* Order Info */}
                                <div className="border-t pt-4 text-xs text-muted-foreground">
                                    <p>N° Commande: <span className="font-mono">{viewingOrder.n_commande}</span></p>
                                    <p>Date: {formatDate(viewingOrder.date_commande)}</p>
                                    <p>Modifié par: {viewingOrder.modified_by_name || '-'}</p>
                                </div>
                            </div>
                        )}
                        
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setViewOrderOpen(false)}>
                                Fermer
                            </Button>
                            {/* Modify button - Vendors can only modify if they claimed/assigned the order */}
                            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur' ||
                              (user?.role === 'vendeur' && (viewingOrder?.claimed_by_vendeur_id === user?.id || viewingOrder?.assigned_vendeur_id === user?.id))) && (
                                <Button className="btn-luxury" onClick={() => {
                                    setViewOrderOpen(false);
                                    handleOpenDialog(viewingOrder);
                                }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                </Button>
                            )}
                            {/* NOTE: "Claim pour modifier" message removed - vendors only see their own orders now */}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                
                {/* Claim for Vendor Dialog (Admin Only) */}
                <Dialog open={claimForVendorDialogOpen} onOpenChange={setClaimForVendorDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>🎯 Assigner la commande à un vendeur</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Assignez et claim cette commande pour un vendeur spécifique.
                            </p>
                            {orderToClaimFor && (
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="font-semibold">{orderToClaimFor.n_commande}</p>
                                    <p className="text-sm">{orderToClaimFor.full_name}</p>
                                    <p className="text-sm text-muted-foreground">{orderToClaimFor.produit}</p>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Vendeur</Label>
                                <Select value={selectedVendorForClaim} onValueChange={setSelectedVendorForClaim}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner un vendeur" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {vendeurs.map(v => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.name} ({v.email})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setClaimForVendorDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleClaimForVendor}
                                disabled={!selectedVendorForClaim}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                🎯 Assigner et Claim
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                
                {/* Reject Order Dialog (Vendeur Only) */}
                <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>❌ Rejeter la commande</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Pourquoi rejetez-vous cette commande ?
                            </p>
                            {orderToReject && (
                                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                    <p className="font-semibold">{orderToReject.n_commande}</p>
                                    <p className="text-sm">{orderToReject.produit}</p>
                                    <p className="text-sm font-semibold text-primary">{formatCurrency(orderToReject.prix)}</p>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label>Raison du rejet *</Label>
                                <Select value={rejectReason} onValueChange={setRejectReason}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner une raison" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="price_too_low">💰 Prix trop bas</SelectItem>
                                        <SelectItem value="product_unavailable">📦 Produit non disponible</SelectItem>
                                        <SelectItem value="other">📝 Autre raison</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {rejectReason === 'price_too_low' && (
                                <div className="space-y-2">
                                    <Label>Prix de vente suggéré (DA)</Label>
                                    <Input
                                        type="number"
                                        placeholder="Ex: 25000"
                                        value={suggestedPrice}
                                        onChange={(e) => setSuggestedPrice(e.target.value)}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        💡 Indiquez le prix auquel vous pourriez vendre ce produit
                                    </p>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label>Notes (optionnel)</Label>
                                <Textarea
                                    placeholder="Informations complémentaires..."
                                    value={rejectNotes}
                                    onChange={(e) => setRejectNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => {
                                setRejectDialogOpen(false);
                                setRejectReason('');
                                setSuggestedPrice('');
                                setRejectNotes('');
                            }}>
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleRejectOrder}
                                disabled={!rejectReason || rejecting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {rejecting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Rejet...
                                    </>
                                ) : (
                                    '❌ Confirmer le rejet'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
